import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, Button, CircularProgress } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const PDFViewer = ({ paperId, paperTitle, apiBaseUrl, source = 'unknown', onClose }) => {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (paperId) {
      loadPDF();
    }
  }, [paperId]);

  useEffect(() => {
    if (pdfDoc) {
      renderPage();
    }
  }, [pdfDoc, currentPage, scale]);

  const loadPDF = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${apiBaseUrl}/api/papers/${paperId}/download-direct?track=false`;
      const loadingTask = pdfjsLib.getDocument(url);
      const pdf = await loadingTask.promise;
      
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setCurrentPage(1);
      setLoading(false);
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError('Failed to load PDF. It may not be available.');
      setLoading(false);
    }
  };

  const renderPage = async () => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale });
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
    } catch (err) {
      console.error('Error rendering page:', err);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, numPages));
  };

  const handleDownload = () => {
    window.open(`${apiBaseUrl}/api/papers/${paperId}/download-direct?source=${source}`, '_blank');
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: '#f5f5f5'
    }}>
      {/* Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 1, 
        borderBottom: '1px solid #ddd',
        bgcolor: 'white',
        gap: 1
      }}>
        <IconButton size="small" onClick={onClose} title="Close PDF viewer">
          <CloseIcon />
        </IconButton>
        
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }} noWrap>
          {paperTitle}
        </Typography>

        {!loading && !error && (
          <>
            <IconButton size="small" onClick={handlePrevPage} disabled={currentPage <= 1}>
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="caption">
              {currentPage} / {numPages}
            </Typography>
            <IconButton size="small" onClick={handleNextPage} disabled={currentPage >= numPages}>
              <NavigateNextIcon />
            </IconButton>

            <Box sx={{ borderLeft: '1px solid #ddd', height: 24, mx: 1 }} />

            <IconButton size="small" onClick={handleZoomOut} disabled={scale <= 0.5}>
              <ZoomOutIcon />
            </IconButton>
            <Typography variant="caption" sx={{ minWidth: 45, textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton size="small" onClick={handleZoomIn} disabled={scale >= 3.0}>
              <ZoomInIcon />
            </IconButton>

            <Box sx={{ borderLeft: '1px solid #ddd', height: 24, mx: 1 }} />

            <Button 
              size="small" 
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              variant="outlined"
            >
              Download
            </Button>
          </>
        )}
      </Box>

      {/* PDF Canvas */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 2
      }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>Loading PDF...</Typography>
          </Box>
        )}
        
        {error && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography color="error">{error}</Typography>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              The PDF may not be accessible in this environment.
            </Typography>
          </Box>
        )}
        
        {!loading && !error && (
          <canvas 
            ref={canvasRef} 
            style={{ 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }} 
          />
        )}
      </Box>
    </Box>
  );
};

export default PDFViewer;

